import { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import OutputOutlinedIcon from '@mui/icons-material/OutputOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { aiTokens } from './aiGeneratorTokens';
import { useTranslation } from '../../i18n/useTranslation';
import type { TranslationKey } from '../../i18n';

interface ConceptStep {
  icon: React.ReactNode;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
}

const STEPS: ConceptStep[] = [
  {
    icon: <PlayCircleOutlineIcon fontSize="small" />,
    titleKey: 'workflow.ai.guide.triggerTitle',
    bodyKey: 'workflow.ai.guide.triggerBody',
  },
  {
    icon: <BuildOutlinedIcon fontSize="small" />,
    titleKey: 'workflow.ai.guide.toolTitle',
    bodyKey: 'workflow.ai.guide.toolBody',
  },
  {
    icon: <PsychologyOutlinedIcon fontSize="small" />,
    titleKey: 'workflow.ai.guide.aiTitle',
    bodyKey: 'workflow.ai.guide.aiBody',
  },
  {
    icon: <CallSplitOutlinedIcon fontSize="small" />,
    titleKey: 'workflow.ai.guide.logicTitle',
    bodyKey: 'workflow.ai.guide.logicBody',
  },
  {
    icon: <OutputOutlinedIcon fontSize="small" />,
    titleKey: 'workflow.ai.guide.outputTitle',
    bodyKey: 'workflow.ai.guide.outputBody',
  },
];

export default function WorkflowConceptsGuide() {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: `1px solid ${aiTokens.accentBorder}`,
        background: open ? '#fff' : aiTokens.accentSoft,
        transition: 'background 0.25s ease',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { background: 'rgba(99,102,241,0.06)' },
        }}
      >
        <LightbulbOutlinedIcon sx={{ color: aiTokens.accent, fontSize: 18 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.825rem', fontWeight: 600, color: aiTokens.accent }}>
            {t('workflow.ai.guide.title')}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 }}>
            {t('workflow.ai.guide.subtitle')}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={open ? 'collapse' : 'expand'}
          sx={{
            color: aiTokens.accent,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s ease',
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={open} timeout={250} unmountOnExit>
        <Box
          sx={{
            px: 2.25,
            pb: 2,
            pt: 0.5,
            borderTop: `1px dashed ${aiTokens.accentBorder}`,
          }}
        >
          <Stack
            spacing={2}
            sx={{
              position: 'relative',
              mt: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 11,
                top: 12,
                bottom: 12,
                width: 1,
                background: `linear-gradient(180deg, ${aiTokens.accent}, transparent)`,
                opacity: 0.4,
                zIndex: 0,
              },
            }}
          >
            {STEPS.map((step, idx) => (
              <Stack
                key={step.titleKey}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{ position: 'relative', zIndex: 1 }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 23,
                    height: 23,
                    borderRadius: '8px',
                    background: '#fff',
                    color: aiTokens.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${aiTokens.accentBorder}`,
                  }}
                >
                  {step.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      color: 'text.primary',
                      letterSpacing: '0.01em',
                      mb: 0.25,
                    }}
                  >
                    <Box component="span" sx={{ color: aiTokens.accent, mr: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                      0{idx + 1}
                    </Box>
                    {t(step.titleKey)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.55 }}>
                    {t(step.bodyKey)}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: '8px',
              background: 'linear-gradient(90deg, rgba(99,102,241,0.06), rgba(236,72,153,0.04))',
              borderLeft: `3px solid ${aiTokens.accent}`,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
              {t('workflow.ai.guide.exampleTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.55, fontStyle: 'italic' }}>
              {t('workflow.ai.guide.exampleBody')}
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
